/**
 * Número escrito como se escreve no Brasil.
 *
 * `81.961` é oitenta e um mil e novecentos e sessenta e um. O `+` do
 * JavaScript lê isso como 81,961 — e o banco, que guarda quilometragem em
 * número inteiro, recusa:
 *
 *   invalid input syntax for type integer: "81.961"
 *
 * Foi assim que o cadastro de veículo quebrou na oficina. Quatro tentativas
 * seguidas, todas com a quilometragem escrita do jeito certo em português, e
 * o carro não vinculava ao cliente.
 *
 * Este leitor já existia — só vivia dentro da importação de planilha, onde
 * ninguém pensou em reaproveitá-lo para o que a pessoa digita na tela. Agora
 * mora sozinho, e quem precisa importa.
 *
 * As regras, para quem for mexer:
 *
 *   "81.961"     → 81961      ponto separando milhar
 *   "1.250,50"   → 1250.5     ponto milhar, vírgula decimal
 *   "1250,50"    → 1250.5     só vírgula: é decimal
 *   "12.50"      → 12.5       ponto com dois dígitos depois: é decimal
 *   "R$ 1.250"   → 1250       símbolo e espaço saem fora
 *   ""           → null       vazio não é zero: quem chama decide
 */
export function numeroBR(v: unknown): number | null {
  if (v == null) return null;
  let t = String(v).trim().replace(/[R$\s]/g, "");
  if (!t || !/\d/.test(t)) return null;

  const pt = t.includes("."), vg = t.includes(",");

  if (pt && vg) {
    // Quem vier por último manda: em pt-BR a vírgula decimal fica à direita.
    t = t.lastIndexOf(",") > t.lastIndexOf(".")
      ? t.replace(/\./g, "").replace(",", ".")
      : t.replace(/,/g, "");
  } else if (vg) {
    t = t.replace(",", ".");
  } else if (pt) {
    /* Só ponto é ambíguo. Trata como milhar quando há mais de um ponto, ou
       quando sobram exatamente três dígitos depois dele e no máximo três
       antes — que é a forma de 81.961 e de 1.250. "12.50" continua sendo
       doze e meio. */
    const p = t.split(".");
    if (p.length > 2 || (p[1]?.length === 3 && p[0].length <= 3)) t = t.replace(/\./g, "");
  }

  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/** Inteiro, para coluna que não aceita casa decimal — quilometragem, ano. */
export const inteiroBR = (v: unknown): number | null => {
  const n = numeroBR(v);
  return n == null ? null : Math.round(n);
};
